import * as React from 'react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/core/lib/Context';
import {
  Dialog,
  DialogType,
  DialogFooter,
  DatePicker,
  DayOfWeek,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Text,
  Link,
  Stack,
} from '@fluentui/react';
import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { PBTEngagementModel } from '../../../Interfaces/IPBTCase';
import { PBTApiEndpoints } from '../../../Services/PBTConstants';
import { usePermission } from '../../../Hooks/useFeature';

const MANAGER_GUIDE_URL = 'https://aka.ms/PBTToolManagerGuide';

interface TermDateDialogProps {
  isOpen: boolean;
  selectedCase: IPBTCase;
  onDismiss: () => void;
  onSuccess: (newDate: string) => void;
}

function getMinDate(): Date {
  // The term conversation date may be scheduled for the current day or later. Weekends are
  // rejected by the DatePicker's onSelectDate handler; this minDate blocks only past dates.
  // (Server-side, the authoritative minimum lead time is enforced from configuration
  // TermConversationDate:MinimumLeadDays.)
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return d;
}

export const TermDateDialog: React.FC<TermDateDialogProps> = ({ isOpen, selectedCase, onDismiss, onSuccess }) => {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  // Broad editors (HRC / HRC Leader / Admin) hold Cases.Edit and may act on any case, not just
  // ones where they are the named manager/HRC. Own-scoped managers hold the narrower
  // Cases.EditTermDate and are still constrained to their own case (isCaseManager + server-side).
  const canEditBroad = usePermission('Cases.Edit');
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [minDate, setMinDate] = React.useState<Date>(() => getMinDate());

  const parseLocalDateOnly = (value?: string | null): Date | undefined => {
    if (!value) return undefined;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  React.useEffect(() => {
    if (isOpen) {
      setMinDate(getMinDate());
      setSelectedDate(parseLocalDateOnly(selectedCase.termConvDate));
      setStatus('idle');
      setErrorMsg(null);
    }
  }, [isOpen, selectedCase.id]);

  const handleSubmit = async () => {
    if (!selectedDate) return;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const user = await authClient.getUser();
      const userAlias = user?.email?.split('@')[0] ?? '';

      // Access gate: the case's assigned manager or assigned HRC may set the term date, and so may
      // a broad editor (HRC / HRC Leader / Admin via Cases.Edit) acting on any case. The actor role
      // is derived below (never a client-picked label) so a caller can't be silently sent as
      // 'Manager' and slip past the backend on a non-bypass case.
      const me = userAlias.trim().toLowerCase();
      const caseManagerAlias = (selectedCase.managerAlias ?? '').trim().toLowerCase();
      const caseHrcAlias = (selectedCase.hrc ?? '').trim().toLowerCase();
      const isCaseManager = me !== '' && me === caseManagerAlias;
      const isAssignedHrc = me !== '' && me === caseHrcAlias;

      if (!isCaseManager && !isAssignedHrc && !canEditBroad) {
        setStatus('error');
        setErrorMsg(
          'You are not the assigned manager or HRC for this case, so you cannot set the term conversation date.'
        );
        return;
      }

      // Pick the actor capacity the backend accepts for this engagement model: HRC-led
      // (manager-bypassed) cases must be set by HRC; every other model by Manager. The server
      // rejects Manager on bypass cases and HRC on non-bypass cases, so a broad editor (Admin)
      // acting on someone else's case is routed to the valid role rather than a stale label.
      const isBypassCase = (selectedCase.engModel ?? '').trim() === PBTEngagementModel.HRCLed;
      let actorRole: string;
      if (isCaseManager) {
        actorRole = 'Manager';
      } else if (isAssignedHrc) {
        actorRole = 'HRC';
      } else {
        actorRole = isBypassCase ? 'HRC' : 'Manager';
      }

      const toLocalDateOnly = (d: Date): string => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const termConversationDate = toLocalDateOnly(selectedDate);

      const res = await fetch(PBTApiEndpoints.UpdateTermConversationDate(selectedCase.id), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          termConversationDate,
          actorAlias: userAlias,
          actorRole,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error?.message ||
          body?.errors?.[0]?.message ||
          body?.message ||
          `Server returned ${res.status}`
        );
      }
      setStatus('success');
      onSuccess(termConversationDate);
    } catch (err) {
      setStatus('error');
      setErrorMsg((err as Error).message);
    }
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={{ type: DialogType.largeHeader, title: 'Select Term Conversation Date' }}
      modalProps={{ isBlocking: true }}
      minWidth={520}
    >
      <Stack tokens={{ childrenGap: 14 }}>
        <MessageBar messageBarType={MessageBarType.info} isMultiline>
          Please review the{' '}
          <Link href={MANAGER_GUIDE_URL} target="_blank" rel="noopener noreferrer">
            Manager Process Guide
          </Link>{' '}
          for details on this process.
        </MessageBar>

        <Text id="term-date-description">
          Select the accurate date of your planned Termination Conversation for the employee below. This date must be
          accurate as it informs critical employment termination documentation.
        </Text>

        <Stack
          tokens={{ childrenGap: 4 }}
          styles={{ root: { background: '#f3f2f1', padding: 12, borderRadius: 4 } }}
          role="group"
          aria-label="Employee details"
        >
          <Text>
            <strong>Employee Alias:</strong> {selectedCase.empAlias || '—'}
          </Text>
          <Text>
            <strong>Employee Name:</strong> {selectedCase.empName}
          </Text>
          <Text>
            <strong>Employee PERN:</strong> {selectedCase.pern}
          </Text>
        </Stack>

        <DatePicker
          label="Planned Termination Conversation Date"
          isRequired
          disabled={status === 'submitting' || status === 'success'}
          firstDayOfWeek={DayOfWeek.Monday}
          placeholder="Select a date..."
          ariaLabel="Planned Termination Conversation Date"
          value={selectedDate}
          onSelectDate={(date) => {
            if (date && (date.getDay() === 0 || date.getDay() === 6)) {
              setStatus('error');
              setErrorMsg('Please select a business day (Mon–Fri).');
              setSelectedDate(undefined);
              return;
            }
            setSelectedDate(date ?? undefined);
            setStatus('idle');
            setErrorMsg(null);
          }}
          minDate={minDate}
        />

        <Stack
          styles={{ root: { background: '#f9f9f9', padding: 12, borderRadius: 4, borderLeft: '3px solid #0078d4' } }}
        >
          <Text styles={{ root: { fontWeight: 600, marginBottom: 6 } }}>Your Immediate Next Steps</Text>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }} aria-label="Next steps after selecting the date">
            <li>
              Schedule a 1:1 with your employee to deliver the termination message.
            </li>
            <li>
              If there is a complexity or concern surrounding delivering this conversation independent of HR, please
              partner with your manager and/or open an AskHR case to discuss with an HR Consultant.
            </li>
          </ul>
        </Stack>

        <div aria-live="polite" aria-atomic="true">
          {status === 'error' && errorMsg && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMsg(null)}>
              {errorMsg}
            </MessageBar>
          )}
          {status === 'success' && (
            <MessageBar messageBarType={MessageBarType.success}>Term conversation date saved successfully.</MessageBar>
          )}
        </div>
      </Stack>

      <DialogFooter>
        {status === 'submitting' ? (
          <Spinner size={SpinnerSize.small} label="Saving..." ariaLive="assertive" />
        ) : status === 'success' ? (
          <DefaultButton text="Close" onClick={onDismiss} />
        ) : (
          <>
            <PrimaryButton text="Confirm Date" onClick={handleSubmit} disabled={!selectedDate} />
            <DefaultButton text="Cancel" onClick={onDismiss} />
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};
